import React, { useState, useEffect} from "react";
import { Button, Collapse, Dropdown } from "react-bootstrap";
import { FaChevronDown } from "react-icons/fa";
import { FaPlus } from "react-icons/fa6";
import { BiSolidEditAlt } from "react-icons/bi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const generateInitialState = () => {
  const days = {};
  for (let i = 1; i <= 30; i++) {
    days[`Day${i}`] = { video1: null, video2: null };
  }
  return days;
};

const DayVideoSelector = () => {
  const [selectedVideos, setSelectedVideos] = useState(generateInitialState());
  const [videoOptions, setVideoOptions] = useState([]);
  const [openCollapse, setOpenCollapse] = useState({
    "1-5": false,
    "6-10": false,
    "11-15": false,
    "16-20": false,
    "21-25": false,
    "26-30": false,
  });

  const [dropdownVisible, setDropdownVisible] = useState({});
  const [videoVisible, setVideoVisible] = useState({});

  // ✅ Fetch videos from API
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/for-you-category/videosByCategoryIdForAdmin`
        );
        const result = await response.json();
  
        if (result.success) {
          const formatted = result.data.map((video) => ({
            id: video.id,
            label: video.title,
            value: video.video,
          }));
          setVideoOptions(formatted);
        } else {
          console.error("Failed to fetch video options");
        }
      } catch (error) {
        console.error("Error fetching video options:", error);
      }
    };
  
    const fetchInitialVideos = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/daily-videos/getAllDailyVideos`);
        const result = await response.json();
        
        // Check if result is an object with a data property or success property
        const videoData = result.data || (result.success ? result : result);
        
        if (videoData) {
          const updated = generateInitialState();
          const newVideoVisible = {};
                    
          // Handle different response formats
          if (Array.isArray(videoData)) {
            videoData.forEach(({ day, videos }) => {
              // Convert day to string if it's a number
              const dayKey = `Day${day}`;
              
              if (videos?.length) {
                // Set video1 if it exists
                if (videos[0]) {
                  updated[dayKey].video1 = videos[0].videoUrl || videos[0].video || null;
                  newVideoVisible[`${dayKey}-video1`] = true;
                }
                
                // Set video2 if it exists
                if (videos[1]) {
                  updated[dayKey].video2 = videos[1].videoUrl || videos[1].video || null;
                  newVideoVisible[`${dayKey}-video2`] = true;
                }
              }
            });
          } else if (typeof videoData === 'object') {
            // Handle object format - iterate through keys
            Object.entries(videoData).forEach(([key, value]) => {
              if (key.includes('Day') && value?.videos?.length) {
                const dayKey = key;
                const videos = value.videos;
                                
                // Set video1 if it exists
                if (videos[0]) {
                  updated[dayKey].video1 = videos[0].videoUrl || videos[0].video || null;
                  newVideoVisible[`${dayKey}-video1`] = true;
                }
                
                // Set video2 if it exists
                if (videos[1]) {
                  updated[dayKey].video2 = videos[1].videoUrl || videos[1].video || null;
                  newVideoVisible[`${dayKey}-video2`] = true;
                }
              }
            });
          }
    
          setSelectedVideos(updated);
          setVideoVisible(prev => ({
            ...prev,
            ...newVideoVisible
          }));
        } else {
          console.error("Invalid response format:", result);
        }
      } catch (error) {
        console.error("Error loading initial videos:", error);
      }
    };
  
    fetchVideos();
    fetchInitialVideos();
  }, []);
  

  const handleSave = async (startDay, endDay) => {
    let successCount = 0;
    let errorCount = 0;
  
    for (let i = startDay; i <= endDay; i++) {
      const dayKey = `Day${i}`;
      const video1 = selectedVideos[dayKey]?.video1;
      const video2 = selectedVideos[dayKey]?.video2;
  
      if (video1 || video2) {
        const payload = {
          day: i,
          videos: [],
        };
  
        if (video1) {
          const videoMeta1 = videoOptions.find((v) => v.value === video1);
          payload.videos.push({
            videoTitle: videoMeta1?.label || `Video 1 - Day ${i}`,
            videoUrl: video1,
          });
        }
  
        if (video2) {
          const videoMeta2 = videoOptions.find((v) => v.value === video2);
          payload.videos.push({
            videoTitle: videoMeta2?.label || `Video 2 - Day ${i}`,
            videoUrl: video2,
          });
        }
  
        try {
          const res = await fetch(`${process.env.REACT_APP_BASE_URL}/daily-videos/saveVideos`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
  
          const result = await res.json();
          
          if (result.success) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Failed to save Day ${i}:`, result.message || "Unknown error");
          }
        } catch (error) {
          errorCount++;
          console.error(`Failed to save Day ${i}:`, error);
        }
      }
    }
    
    // Show success toast if any days were saved successfully
    if (successCount > 0) {
      toast.success(`Videos have been successfully saved!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
    
    // Show error toast if any days failed to save
    if (errorCount > 0) {
      toast.error(`Failed to save videos for ${errorCount} day(s). Check console for details.`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };  

  const handlePlusClick = (day, videoSlot) => {
    setDropdownVisible((prev) => ({ 
      ...prev, 
      [`${day}-${videoSlot}`]: true 
    }));
  };

  const handleOptionSelect = (day, videoSlot, optionValue) => {
    const updated = { ...selectedVideos };
    updated[day][videoSlot] = optionValue;
    setSelectedVideos(updated);
    setDropdownVisible((prev) => ({ 
      ...prev, 
      [`${day}-${videoSlot}`]: false 
    }));
    setVideoVisible((prev) => ({ 
      ...prev, 
      [`${day}-${videoSlot}`]: true 
    }));
  };

  const handleEditClick = (day, videoSlot) => {
    setDropdownVisible((prev) => ({ 
      ...prev, 
      [`${day}-${videoSlot}`]: true 
    }));
    setVideoVisible((prev) => ({ 
      ...prev, 
      [`${day}-${videoSlot}`]: false 
    }));
  };

  const toggleCollapse = (section) => {
    setOpenCollapse(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Helper function to render days in each group
  const renderDayGroup = (startDay, endDay) => {
    const days = [];
    for (let i = startDay; i <= endDay; i++) {
      days.push(i);
    }

    return days.map((dayNum) => {
      const dayKey = `Day${dayNum}`;
      return (
        <div className="dayItem" key={dayKey}>
          <p className="dayNo">{dayKey}</p>
          <div className="row w-100">
            {/* First Video */}
            <div className="col-md-6 VideoBox">
              {renderVideoSlot(dayKey, "video1")}
            </div>
            
            {/* Second Video */}
            <div className="col-md-6 VideoBox">
              {renderVideoSlot(dayKey, "video2")}
            </div>
          </div>
        </div>
      );
    });
  };

  // Helper function to render a single video slot
  const renderVideoSlot = (dayKey, videoSlot) => {
    const selectedVideo = selectedVideos[dayKey][videoSlot];
    const dropdownKey = `${dayKey}-${videoSlot}`;
    const isVideoVisible = videoVisible[dropdownKey];
    const isDropdownVisible = dropdownVisible[dropdownKey];
    
    return (
      <>
        {/* Plus Button */}
        {!isDropdownVisible && !selectedVideo && !isVideoVisible && (
          <button
            type="button"
            className="PlusBtn"
            onClick={() => handlePlusClick(dayKey, videoSlot)}
          >
            <FaPlus />
          </button>
        )}

        {/* Dropdown */}
        {isDropdownVisible && (
          <Dropdown
            onSelect={(eventKey) => handleOptionSelect(dayKey, videoSlot, eventKey)}
          >
            <Dropdown.Toggle
              variant="secondary"
              className="VideoDropdown"
              id={`dropdown-${dropdownKey}`}
            >
              {selectedVideo ? "Change video" : "Select a video"}
            </Dropdown.Toggle>
            <Dropdown.Menu>
            {videoOptions.map((video) => (
              <Dropdown.Item key={video.id} eventKey={video.value}>
                {video.label}
              </Dropdown.Item>
            ))}
            </Dropdown.Menu>
          </Dropdown>
        )}

        {/* Video Player & Edit */}
        {selectedVideo && isVideoVisible && (
          <div className="video-wrapper mt-3">
            <iframe
              width="100%"
              height="200"
              src={selectedVideo}
              title="Video Preview"
              frameBorder="0"
              allowFullScreen
            ></iframe>
            <Button
              variant="outline-primary"
              onClick={() => handleEditClick(dayKey, videoSlot)}
              className="mt-2 EditBtn"
            >
              <BiSolidEditAlt />
              Edit
            </Button>
          </div>
        )}
      </>
    );
  };

  // Create an array of day ranges for all 30 days
  const dayRanges = [
    { label: "Days 1 - 5", range: "1-5", start: 1, end: 5 },
    { label: "Days 6 - 10", range: "6-10", start: 6, end: 10 },
    { label: "Days 11 - 15", range: "11-15", start: 11, end: 15 },
    { label: "Days 16 - 20", range: "16-20", start: 16, end: 20 },
    { label: "Days 21 - 25", range: "21-25", start: 21, end: 25 },
    { label: "Days 26 - 30", range: "26-30", start: 26, end: 30 },
  ];

  return (
    <div className="day-video-selector">
      <ToastContainer />
      {dayRanges.map((group) => (
        <div key={group.range} className="mb-3">
          <Button
            onClick={() => toggleCollapse(group.range)}
            aria-controls={`collapse-${group.range}`}
            aria-expanded={openCollapse[group.range]}
            className="customBtn"
          >
            <span>{group.label}</span>
            <span className="chevron-icon">
              <FaChevronDown />
            </span>
          </Button>

          <Collapse in={openCollapse[group.range]} className="customCollapse">
            <div id={`collapse-${group.range}`}>
              <div className="w-100">
              <button
                type="button"
                className="SaveBtn ms-auto"
                onClick={() => handleSave(group.start, group.end)}
              >
                Save
              </button>
              </div>
              {renderDayGroup(group.start, group.end)}
            </div>
          </Collapse>
        </div>
      ))}
    </div>
  );
};

export default DayVideoSelector;